import { livesGetEmergencyContact, livesSubmitEmergencyContact } from '@/app/services/live';
import type { LiveV1SubmitEmergencyContactRequest } from '@/app/services/live/types.gen';
import {
  type InspirationsGetCollectInspirationsListData,
  type InspirationsGetCollectInspirationsListResponse,
  type InspirationsGetLikeInspirationsListData,
  type InspirationsGetLikeInspirationsListResponse,
  type InspirationsGetMyInspirationsListData,
  type InspirationsGetPersonaInspirationsListData,
  inspirationsGetCollectInspirationsList,
  inspirationsGetLikeInspirationsList,
  inspirationsGetMyInspirationsList,
  inspirationsGetPersonaInspirationsList,
  type MaterialsShareElementsFeedData,
  type MaterialsShareElementsFeedResponse,
  materialsGetMyShareElements,
  materialsGetPersonaShareElements,
  materialsShareElementsFeed,
  type UsersGetMyFollowersData,
  type UsersGetMyFollowersResponse,
  type UsersGetMyFollowingsData,
  type UsersGetMyFollowingsResponse,
  usersGetMyFollowers,
  usersGetMyFollowings,
  usersGetPersona,
} from '@/app/services/vidu';

export { usersDelete, usersUpdateProfile } from '@/app/services/iam';
export const getLiveEmergencyContact = () => livesGetEmergencyContact();
export const submitLiveEmergencyContact = (body: LiveV1SubmitEmergencyContactRequest) =>
  livesSubmitEmergencyContact({ body });
export { favorsBatchFavorite } from '@/app/services/tools';
export {
  materialsGetMyShareElements,
  materialsGetPersonaShareElements,
  materialsShareElementsFeed,
} from '@/app/services/vidu';

import type { ApiQueryParams, ServiceError } from '@/app/types';
import type {
  FetchPostReferenceParams,
  GetOthersSubmissionQuery,
  GetOthersSubmissionResponse,
  GetSubmissionQuery,
  GetSubmissionResponse,
} from './types';

export { viduExploreDetailPro as apiGetDetail } from '@/app/services/vidu';

export const apiGetMyInspiration = (
  query: ApiQueryParams<InspirationsGetMyInspirationsListData, 'types' | 'pager.page' | 'pager.pagesz'>,
) => {
  return inspirationsGetMyInspirationsList({
    query: query,
  }).then(({ data, error, ...params }) => {
    if (!data || !data?.list || error) return { error, ...params };
    return { data };
  });
};

import { usersGetMyStat } from '@/app/services/vidu';

export const apiGetMyPersonalInfo = () => {
  return usersGetMyStat();
};

/**
 * 我的短片投稿
 * @param query
 * @returns
 */
export const apiGetMyShortFilmSubmissions = ({ query }: { query: GetSubmissionQuery }): GetSubmissionResponse => {
  return apiGetMyInspiration({
    types: ['short_film'],
    ...query,
  });
};

/**
 * 我的教程投稿
 * @param query
 * @returns
 */
export const apiGetMyTutorialSubmissions = ({ query }: { query: GetSubmissionQuery }): GetSubmissionResponse => {
  return apiGetMyInspiration({
    types: ['tutorial'],
    ...query,
  });
};

/**
 * 我的主体投稿
 * @param param0
 * @returns
 */
export const apiGetMyPostReferences = ({ query }: FetchPostReferenceParams) => {
  return materialsGetMyShareElements({
    query: query,
  });
};

/**
 * 我的灵感投稿
 * @param query
 * @returns
 */
export const apiGetMyInspirationSubmissions = ({ query }: { query: GetSubmissionQuery }): GetSubmissionResponse => {
  return apiGetMyInspiration({
    types: ['media_asset', 'image_asset', 'mini_video'],
    ...query,
  });
};

/**
 * 我的点赞
 * @param query
 * @returns
 */
export const apiGetMyLikes = (
  query: ApiQueryParams<InspirationsGetLikeInspirationsListData, 'types' | 'pager.page' | 'pager.pagesz'>,
): Promise<{
  data?: InspirationsGetLikeInspirationsListResponse;
  error?: ServiceError;
}> => {
  return inspirationsGetLikeInspirationsList({ query }).then(({ data, error, ...params }) => {
    if (!data || !data?.list || error) return { error, ...params };
    return { data };
  });
};

export const apiGetOtherPersonalInfo = (userId?: string) => {
  return usersGetPersona({
    path: {
      user_id: userId || '',
    },
  });
};

export const apiGetOthersInspiration = ({
  query,
  path,
}: {
  query: ApiQueryParams<InspirationsGetPersonaInspirationsListData, 'types' | 'pager.page' | 'pager.pagesz'>;
  path: InspirationsGetPersonaInspirationsListData['path'];
}) => {
  return inspirationsGetPersonaInspirationsList({
    query: query,
    path,
  }).then(({ data, error, ...params }) => {
    if (!data || !data?.list || error) return { error, ...params };
    return { data };
  });
};

/**
 * 其他人的短片投稿
 * @param query
 * @returns
 */
export const apiGetOthersShortFilmSubmissions = ({
  path,
  query,
}: GetOthersSubmissionQuery): GetOthersSubmissionResponse => {
  return apiGetOthersInspiration({
    query: { types: ['short_film'], ...query },
    path,
  });
};

/**
 * 其他人的教程投稿
 * @param query
 * @returns
 */
export const apiGetOthersTutorialSubmissions = ({
  path,
  query,
}: GetOthersSubmissionQuery): GetOthersSubmissionResponse => {
  return apiGetOthersInspiration({
    query: { types: ['tutorial'], ...query },
    path,
  });
};

/**
 * 其他人的主体投稿
 * @param param0
 * @returns
 */
export const apiGetOthersPostReferences = ({ query, path }: FetchPostReferenceParams) => {
  return materialsGetPersonaShareElements({
    query: query,
    path,
  });
};

/**
 * 其他人的灵感投稿
 * @param query
 * @returns
 */
export const apiGetOthersInspirationSubmissions = ({
  path,
  query,
}: GetOthersSubmissionQuery): GetOthersSubmissionResponse => {
  return apiGetOthersInspiration({
    query: { types: ['media_asset', 'image_asset', 'mini_video'], ...query },
    path,
  });
};

/**
 * 获取共享主体收藏
 * @param query
 * @returns
 */
export const apiGetShareElements = (
  query: ApiQueryParams<
    MaterialsShareElementsFeedData,
    'pager.page' | 'pager.pagesz' | 'pager.page_token' | 'is_collect'
  >,
): Promise<{
  data?: MaterialsShareElementsFeedResponse;
  error?: ServiceError;
}> => {
  return materialsShareElementsFeed({ query }).then(({ data, error, ...params }) => {
    if (!data || !data?.categories || error) return { error, ...params };
    return { data };
  });
};

/**
 * 收藏我的收藏
 * @param query
 * @returns
 */
export const apiGetMyCollect = (
  query: ApiQueryParams<InspirationsGetCollectInspirationsListData, 'types' | 'pager.page' | 'pager.pagesz'>,
): Promise<{
  data?: InspirationsGetCollectInspirationsListResponse;
  error?: ServiceError;
}> => {
  return inspirationsGetCollectInspirationsList({ query }).then(({ data, error, ...params }) => {
    if (!data || !data?.list || error) return { error, ...params };
    return { data };
  });
};

/**
 * 获取我的粉丝
 * @param query
 * @returns
 */
export const apiGetMyFollowers = (
  query: ApiQueryParams<UsersGetMyFollowersData, 'pager.page_token' | 'pager.pagesz'>,
): Promise<{
  data?: UsersGetMyFollowersResponse;
  error?: ServiceError;
}> => {
  return usersGetMyFollowers({ query }).then(({ data, error, ...params }) => {
    if (!data || !data?.follower_list || error) return { error, ...params };
    return { data };
  });
};

/**
 * 获取我关注的用户
 * @param query
 * @returns
 */
export const apiGetMyFollowings = (
  query: ApiQueryParams<UsersGetMyFollowingsData, 'pager.page_token' | 'pager.pagesz'>,
): Promise<{
  data?: UsersGetMyFollowingsResponse;
  error?: ServiceError;
}> => {
  return usersGetMyFollowings({ query }).then(({ data, error, ...params }) => {
    if (!data || !data?.following_list || error) return { error, ...params };
    return { data };
  });
};
